<template>
  <v-row class="text-left full-height">
    <v-col cols="12">
      <v-card flat class="my-2 ml-3 mr-2">
        <v-form
          ref="companyInfoForm"
          v-model="form.validCompanyInfo"
          onSubmit="return false;"
          class="full-width"
        >
          <v-row>
            <v-col cols="12" sm="12" md="6" class="px-0 py-4">
              <Typography variant="subtitle-1">Visible Pages</Typography>
              <v-card-text>
                <v-select
                  v-model="initialRouteOnLoad"
                  :items="routablePagesOnLoad"
                  label="Default Landing Page"
                  item-value="value"
                  item-text="label"
                  outlined
                  hide-details
                  color="brandPrimary"
                  class="mt-3 full-width"
                ></v-select>
                <v-switch
                  label="Display activity page"
                  v-model="displayActivityPage"
                  color="brandPrimary"
                  hide-details
                />
                <v-switch
                  label="Display wallet page"
                  v-model="displayWalletPage"
                  color="brandPrimary"
                  hide-details
                />
                <v-switch
                  label="Display program page"
                  v-model="displayProgramPage"
                  color="brandPrimary"
                  hide-details
                />
                <v-switch
                  label="Display dashboard page"
                  v-model="displayProgramDashboardPage"
                  color="brandPrimary"
                  hide-details
                />
                <v-switch
                  v-if="canManageProgramSetup"
                  label="Display claims page"
                  v-model="displayClaimPage"
                  color="brandPrimary"
                  hide-details
                />
                <v-switch
                  label="Display library page"
                  v-model="displayLibraryPage"
                  color="brandPrimary"
                  hide-details
                />
              </v-card-text>
            </v-col>
            <v-col cols="12" sm="12" md="6" class="px-0">
              <Typography variant="subtitle-1">Program Options</Typography>
              <v-card-text>
                <v-switch
                  v-if="canManageProgramSetup"
                  label="Hide default platform logo"
                  v-model="hideDefaultPlatformLogo"
                  color="brandPrimary"
                  hide-details
                />
                <v-switch
                  v-if="canManageProgramSetup"
                  label="Procore client"
                  v-model="procoreEnabled"
                  color="brandPrimary"
                  hide-details
                />
                <v-switch
                  label="Display universal learning"
                  v-model="displayUniversalLearning"
                  color="brandPrimary"
                  hide-details
                />

                <v-switch
                  v-if="isWhistleAdmin"
                  label="Display mobile popup on web"
                  v-model="displayMobilePopup"
                  color="brandPrimary"
                  hide-details
                />
                <v-switch
                  label="Magic links enabled"
                  v-model="magicLinksEnabled"
                  color="brandPrimary"
                  hide-details
                />
                <v-switch
                  label="Recognitions enabled"
                  v-model="recognitionsEnabled"
                  color="brandPrimary"
                />
                <v-select
                  v-if="isWhistleAdmin"
                  v-model="onboardingLanguage"
                  :items="onboardingLanguageOptions"
                  label="Onboarding Version"
                  item-value="value"
                  item-text="label"
                  outlined
                  color="brandPrimary"
                  class="full-width"
                ></v-select>
              </v-card-text>
            </v-col>
          </v-row>
          <v-row v-if="showOne10Points || canManageProgramSetup || displayAnnualPaymentCap">
            <v-col cols="12" sm="12" md="12" class="px-0 py-4">
              <Typography variant="subtitle-1">Program & Financial Settings</Typography>
              <v-row class="ma-0 pt-6">
                <v-col cols="12" sm="12" md="6" class="px-4 py-0" v-if="canManageProgramSetup">
                  <v-switch
                    v-if="showOne10Points"
                    class="mt-0 pt-0"
                    v-model="one10PointsEnabled"
                    label="Enable One10 Points"
                    color="brandPrimary"
                  />
                  <v-switch
                    v-if="showOne10Points && one10PointsEnabled"
                    class="mt-0 pt-0 mb-5"
                    v-model="paymentCardEnabled"
                    label="Enable Payment Card"
                    color="brandPrimary"
                    hint="When off, only points based budgets and awards will be allowed"
                    persistent-hint
                  />
                  <v-text-field
                    v-if="one10PointsEnabled"
                    v-model="pointRatio"
                    :rules="formRules.amount"
                    label="Points to Cash Ratio*"
                    color="brandPrimary"
                    type="number"
                    outlined
                    suffix=": $1.00"
                  ></v-text-field>
                  <v-text-field
                    v-model="programNumber"
                    :rules="formRules.stringRequired(255)"
                    label="Program Number*"
                    color="brandPrimary"
                    outlined
                  />
                  <v-select
                    v-if="isWhistleAdmin"
                    v-model="clientClassification"
                    :items="clientClassificationOptions"
                    :rules="[(v) => !!v || 'Client Classification is required']"
                    item-value="value"
                    item-text="label"
                    label="Client ID Classification*"
                    color="brandPrimary"
                    outlined
                  ></v-select>
                  <DynamicInputList
                    v-if="showPspsInputList"
                    :items="pspsNumbers"
                    :rules="pspsRules"
                    :loading="loadingPspsNumbers"
                    itemKey="pspsNumber"
                    :canDelete="(item) => !item.pspsNumberId"
                    @add="addPspsNumber"
                    @delete="deletePspsNumber"
                  />
                </v-col>
                <v-col cols="12" sm="12" md="6" class="px-4 py-0">
                  <v-text-field
                    :style="{
                      paddingTop:
                        !$vuetify.breakpoint.smAndDown && showOne10Points && one10PointsEnabled
                          ? '112px'
                          : '0px',
                    }"
                    v-if="canManageProgramSetup"
                    v-model="physicalCardFee"
                    :rules="formRules.optionalAmount(true)"
                    label="Physical payment card fee"
                    color="brandPrimary"
                    type="number"
                    outlined
                    prefix="$"
                    placeholder="0"
                    hint="Charged once per user for a physical card"
                  ></v-text-field>
                  <v-text-field
                    v-if="showAnnualPaymentCap"
                    v-model="annualPaymentCap"
                    :rules="formRules.optionalAmount(false)"
                    label="Annual Payment Cap"
                    color="brandPrimary"
                    type="number"
                    outlined
                    prefix="$"
                    placeholder="0"
                    hint="Maximum amount a single user can receive per year"
                  ></v-text-field>
                  <v-text-field
                    v-if="showOne10Points && one10PointsEnabled && isOne10PointsToCardEnabled()"
                    v-model="minOne10PointsToCashTransferAmount"
                    :rules="formRules.optionalAmount(true)"
                    label="Minimum Transfer to Card"
                    color="brandPrimary"
                    type="number"
                    outlined
                    prefix="$"
                    placeholder="0"
                    hint="Minimum amount a user can transfer per transaction"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <div class="d-flex justify-center flex-column align-center">
            <Button
              @click="handleSave"
              :disabled="!form.validCompanyInfo"
              :loading="loading.companyInfo"
              depressed
              rounded
              color="brandPrimary"
              class="white--text my-4"
              >Save Changes</Button
            >
            <Typography variant="body-2" v-if="error.companyInfo" class="error--text">
              {{ error.companyInfo }}
            </Typography>
          </div>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import DynamicInputList from "@/components/generic/form-inputs/DynamicInputList.vue";

import { formRules, clientClassificationOptions } from "@/shared_data/data";
import isOne10PointsEnabled from "@/mixins/one10Points/isOne10PointsEnabled";
import getPSPSNumbers from "@/mixins/one10Points/getPSPSNumbers";
import featureFlagsMixin from "@/mixins/featureFlags";
import ClientService from "@/services/ClientServiceV2";
import canManageProgramSetup from "@/mixins/canManageProgramSetup";

import { mapState } from "vuex";

function initialState() {
  return {
    clientService: new ClientService(),

    // Client info
    clientId: null,
    initialRouteOnLoad: null,
    onboardingLanguage: null,
    displayActivityPage: null,
    displayWalletPage: null,
    displayLibraryPage: null,
    displayProgramPage: null,
    displayProgramDashboardPage: null,
    displayClaimPage: null,
    displayUniversalLearning: null,
    magicLinksEnabled: null,
    recognitionsEnabled: null,
    hideDefaultPlatformLogo: null,
    displayMobilePopup: null,
    procoreEnabled: null,
    one10PointsEnabled: null,
    paymentCardEnabled: true,
    programNumber: null,
    pointRatio: null,
    physicalCardFee: null,
    annualPaymentCap: null,
    minOne10PointsToCashTransferAmount: null,
    clientClassification: null,

    // Static data
    onboardingLanguageOptions: [
      { label: "Payments", value: "PAYMENTS" },
      { label: "Learning", value: "LEARNING" },
    ],
    clientClassificationOptions,
    formRules,

    // Dialogs, API, and forms
    form: {
      validCompanyInfo: true,
    },
    error: {
      companyInfo: null,
    },
    loading: {
      companyInfo: false,
    },
  };
}

export default {
  name: "ProgramSetupManager",
  mixins: [isOne10PointsEnabled, featureFlagsMixin, getPSPSNumbers, canManageProgramSetup],
  props: {
    client: {
      type: Object,
      required: false,
    },
  },
  components: {
    Typography,
    Button,
    DynamicInputList,
  },
  data() {
    return initialState();
  },
  created() {
    this.loadClient();
  },
  methods: {
    resetPage() {
      Object.assign(this.$data, initialState());
    },
    loadClient() {
      this.resetPage();

      if (!this.client) {
        console.error("Client not found", this.globalClientId);
        return;
      }
      console.log("Loading client", this.client);

      const {
        clientId,
        initialRouteOnLoad,
        onboardingLanguage,
        displayActivityPage,
        displayWalletPage,
        displayLibraryPage,
        displayProgramPage,
        displayProgramDashboardPage,
        displayClaimPage,
        annualPaymentCap,
        physicalCardFee,
        one10PointsEnabled,
        paymentCardEnabled,
        programNumber,
        pointRatio,
        displayUniversalLearning,
        magicLinksEnabled,
        recognitionsEnabled,
        hideDefaultPlatformLogo,
        displayMobilePopup,
        procoreEnabled,
        minOne10PointsToCashTransferAmount,
        clientClassification,
      } = this.client;

      this.clientId = clientId;
      this.initialRouteOnLoad = initialRouteOnLoad;
      this.onboardingLanguage = onboardingLanguage;
      this.displayActivityPage = displayActivityPage;
      this.displayWalletPage = displayWalletPage;
      this.displayLibraryPage = displayLibraryPage;
      this.displayProgramPage = displayProgramPage;
      this.displayProgramDashboardPage = displayProgramDashboardPage;
      this.displayClaimPage = displayClaimPage;
      this.annualPaymentCap = annualPaymentCap;
      this.physicalCardFee = physicalCardFee;
      this.one10PointsEnabled = one10PointsEnabled;
      this.paymentCardEnabled = paymentCardEnabled;
      this.programNumber = programNumber;
      this.pointRatio = pointRatio;
      this.displayUniversalLearning = displayUniversalLearning;
      this.magicLinksEnabled = magicLinksEnabled;
      this.recognitionsEnabled = recognitionsEnabled;
      this.hideDefaultPlatformLogo = hideDefaultPlatformLogo;
      this.displayMobilePopup = displayMobilePopup;
      this.procoreEnabled = procoreEnabled;
      this.minOne10PointsToCashTransferAmount = minOne10PointsToCashTransferAmount;
      this.clientClassification = clientClassification;

      // Loads existing numbers for all admins, not only when One10 Points is enabled
      if (this.showPspsInputList && this.canManageProgramSetup) {
        this.getPspsNumbers(this.clientId, true, (pspsNumbers) => {
          if (pspsNumbers.length === 0) {
            this.addPspsNumber();
          }
          this.revalidateCompanyInfo();
        });
      } else {
        this.revalidateCompanyInfo();
      }
    },
    revalidateCompanyInfo() {
      this.$nextTick(() => {
        this.$refs.companyInfoForm?.validate();
      });
    },
    addPspsNumber() {
      this.pspsNumbers.push({ pspsNumber: "", clientId: this.clientId });
    },
    deletePspsNumber(index) {
      this.pspsNumbers.splice(index, 1);
    },
    async handleSave() {
      if (!this.form.validCompanyInfo) return;

      try {
        this.loading.companyInfo = true;
        this.error.companyInfo = null;

        let body = {
          initialRouteOnLoad: this.initialRouteOnLoad,
          displayActivityPage: this.displayActivityPage,
          displayWalletPage: this.displayWalletPage,
          displayLibraryPage: this.displayLibraryPage,
          displayProgramPage: this.displayProgramPage,
          displayProgramDashboardPage: this.displayProgramDashboardPage,
          displayClaimPage: this.displayClaimPage,
          annualPaymentCap: this.annualPaymentCap || null, // handles empty strings
          physicalCardFee: this.physicalCardFee || null, // handles empty strings
          one10PointsEnabled: this.one10PointsEnabled,
          paymentCardEnabled: this.one10PointsEnabled ? this.paymentCardEnabled : true,
          programNumber: this.programNumber,
          displayUniversalLearning: this.displayUniversalLearning,
          magicLinksEnabled: this.magicLinksEnabled,
          recognitionsEnabled: this.recognitionsEnabled,
          minOne10PointsToCashTransferAmount: this.minOne10PointsToCashTransferAmount,
          clientClassification: this.clientClassification,
        };

        // Only include pointRatio when One10 Points is enabled
        if (this.one10PointsEnabled) {
          body.pointRatio = this.pointRatio || null;
        }

        if (this.isWhistleAdmin) {
          body.onboardingLanguage = this.onboardingLanguage;
          body.displayMobilePopup = this.displayMobilePopup;
        }
        if (this.canManageProgramSetup) {
          body.procoreEnabled = this.procoreEnabled;
          body.hideDefaultPlatformLogo = this.hideDefaultPlatformLogo;
        }
        console.log("Saving client", body);

        if (this.showPspsInputList && this.pspsNumbers.length) {
          await this.createPSPSNumbers();
        }

        await this.clientService.updateClientV3(this.clientId, body);

        console.log("company info saved", this.clientId);

        await this.$emit("getClient", { clientId: this.clientId });
      } catch (e) {
        this.error.companyInfo = e.message || "Failed to save company info";
      } finally {
        this.loading.companyInfo = false;
      }
    },
    async createPSPSNumbers() {
      try {
        const pspsPayload = [];

        this.pspsNumbers.forEach((n) => {
          if (n.pspsNumber && n.pspsNumber.trim() !== "") {
            pspsPayload.push({
              clientId: this.clientId,
              pspsNumber: n.pspsNumber,
              pspsNumberId: n.pspsNumberId, // undefined if new
              status: "Active",
            });
          }
        });

        if (pspsPayload.length) {
          await this.budgetServiceV3.createBudgetPSPSNumbers(pspsPayload);
        }
      } catch (e) {
        console.error("Failed to create PSPS numbers", e);
      }
    },
  },
  computed: {
    ...mapState(["userProfile", "roles", "globalClientId", "permissions", "featureFlags"]),
    routablePagesOnLoad() {
      var arr = [];
      if (this.displayActivityPage) arr.push({ label: "Activity", value: "activity" });
      if (this.displayLibraryPage) arr.push({ label: "Library", value: "learning" });
      if (this.displayWalletPage) arr.push({ label: "Wallet", value: "wallet" });
      if (this.displayProgramPage) arr.push({ label: "Programs", value: "programs" });
      if (this.displayProgramDashboardPage)
        arr.push({ label: "Dashboard", value: "program-dashboard" });
      if (this.displayClaimPage) arr.push({ label: "Claims", value: "claims" });
      return arr;
    },
    showPspsInputList() {
      return !this.isPspsLookupEnabled();
    },
    pspsRules() {
      return [
        ...this.formRules.stringRequired(255),
        ...this.formRules.preventDuplicateStrings(
          this.pspsNumbers,
          "pspsNumber",
          "is a duplicate PSPS number"
        ),
      ];
    },
    showAnnualPaymentCap() {
      return !!(this.permissions || []).find((x) => x == "clients:create:annualpaymentcap");
    },
    showOne10Points() {
      return !!this.isOne10PointsEnabled(this.clientId) && this.isWhistleAdmin;
    },
    displayAnnualPaymentCap() {
      return this.showAnnualPaymentCap;
    },
  },
  watch: {
    client: {
      handler() {
        console.log("client changed", this.client);
        this.loadClient();
      },
    },
  },
};
</script>

<style scoped></style>
